/* eslint-disable @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-argument */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Plus, ExternalLink, Building2, Copy, CheckCircle2, Zap, Play } from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import type { Project, Organization } from '@/lib/graphql/generated';

interface Props {
  initialProjects: Project[];
  orgs: Organization[];
}

export function AppsClient({
  initialProjects,
  orgs,
}: Props): import('react/jsx-runtime').JSX.Element {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [appName, setAppName] = useState('');
  const [orgId, setOrgId] = useState(orgs[0]?.id ?? '');
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  const projects = initialProjects;

  // Group projects by org
  const projectsByOrg = new Map<string, Project[]>();
  const unassigned: Project[] = [];

  for (const project of projects) {
    const id = project.organization?.id;
    if (id != null) {
      const list = projectsByOrg.get(id) ?? [];
      list.push(project);
      projectsByOrg.set(id, list);
    } else {
      unassigned.push(project);
    }
  }

  const totalCount = projects.length;

  async function handleCreate(): Promise<void> {
    if (!appName.trim()) return;
    setCreating(true);
    setError('');
    try {
      const res = await fetch('/api/projects', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: appName.trim(), org_id: orgId || undefined }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? 'Failed to create app');
      }
      await res.json();
      setOpen(false);
      setAppName('');
      window.location.reload();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setCreating(false);
    }
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold">Apps</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {totalCount} app{totalCount !== 1 ? 's' : ''} across {orgs.length} organization
            {orgs.length !== 1 ? 's' : ''}
          </p>
        </div>
        <Button size="sm" onClick={(): void => setOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          New App
        </Button>
      </div>

      {/* Org sections */}
      {orgs.map((org): import('react/jsx-runtime').JSX.Element => {
        const orgApps = projectsByOrg.get(org.id!) ?? [];
        return (
          <div key={org.id} className="space-y-2">
            <div className="flex items-center gap-2">
              <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-sm font-medium">{org.name}</span>
              <span className="text-xs text-muted-foreground ml-auto">
                {orgApps.length} app{orgApps.length !== 1 ? 's' : ''}
              </span>
            </div>
            <div className="grid gap-2">
              {orgApps.map((project): import('react/jsx-runtime').JSX.Element => (
                <AppRow key={project.id} project={project} />
              ))}
              {orgApps.length === 0 && (
                <div className="rounded-lg border border-dashed py-6 text-center">
                  <p className="text-xs text-muted-foreground">No apps in this organization.</p>
                  <Button
                    size="sm"
                    variant="outline"
                    className="mt-2"
                    onClick={(): void => {
                      setOrgId(org.id!);
                      setOpen(true);
                    }}
                  >
                    Create first app
                  </Button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Unassigned / personal */}
      {unassigned.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">Personal</span>
          </div>
          <div className="grid gap-2">
            {unassigned.map((project): import('react/jsx-runtime').JSX.Element => (
              <AppRow key={project.id} project={project} />
            ))}
          </div>
        </div>
      )}

      {totalCount === 0 && (
        <div className="flex flex-col items-center justify-center rounded-lg border border-dashed py-16 text-center">
          <p className="text-sm text-muted-foreground">No apps yet</p>
          <Button size="sm" variant="outline" className="mt-3" onClick={(): void => setOpen(true)}>
            Create your first app
          </Button>
        </div>
      )}

      {/* New App Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>New App</DialogTitle>
            <DialogDescription>Give your app a name to get started.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="app-name">App name</Label>
              <Input
                id="app-name"
                placeholder="my-app"
                value={appName}
                onChange={(e): void => setAppName(e.target.value)}
                onKeyDown={(e): void => {
                  if (e.key === 'Enter') void handleCreate();
                }}
                autoFocus
              />
            </div>
            {orgs.length > 1 && (
              <div className="space-y-1.5">
                <Label>Organization</Label>
                <select
                  value={orgId}
                  onChange={(e): void => setOrgId(e.target.value)}
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
                >
                  {orgs.map((org): import('react/jsx-runtime').JSX.Element => (
                    <option key={org.id} value={org.id!}>
                      {org.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {error && <p className="text-xs text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={(): void => setOpen(false)} disabled={creating}>
              Cancel
            </Button>
            <Button onClick={handleCreate} disabled={creating || !appName.trim()}>
              {creating ? 'Creating…' : 'Create App'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function AppRow({ project }: { project: Project }): import('react/jsx-runtime').JSX.Element {
  const [copied, setCopied] = useState(false);

  function copyId(e: React.MouseEvent): void {
    e.preventDefault();
    e.stopPropagation();
    if (project.id != null) {
      void navigator.clipboard.writeText(project.id);
      setCopied(true);
      setTimeout((): void => setCopied(false), 2000);
    }
  }

  return (
    <Link to={`/projects/${project.id}`}>
      <Card className="bg-glass-card border-white/5 transition-all duration-300 hover:bg-white/[0.04] hover:border-white/10 group">
        <CardContent className="flex items-center justify-between p-4">
          <div className="flex items-center gap-4">
            <div className="h-9 w-9 rounded-lg bg-accent/50 flex items-center justify-center text-[var(--flux-accent)] group-hover:scale-110 transition-transform">
              <Zap className="h-5 w-5 fill-current" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <p className="font-semibold text-sm tracking-tight">{project.name}</p>
                <div className="flex items-center gap-1 rounded-full bg-emerald-500/10 px-1.5 py-0.5 text-[10px] font-medium text-emerald-400 border border-emerald-500/20 glow-success">
                  <Play className="h-2.5 w-2.5 fill-current" />
                  Live Replay Active
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1">
                <p className="text-[10px] text-muted-foreground font-mono">{project.id}</p>
                <button
                  onClick={copyId}
                  className="p-1 hover:bg-white/5 rounded transition-colors text-muted-foreground hover:text-foreground"
                >
                  {copied ? (
                    <CheckCircle2 className="h-3 w-3 text-emerald-400" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-6">
            <div className="hidden sm:flex flex-col items-end">
              <p className="text-[10px] uppercase tracking-wider text-muted-foreground/50 font-medium">
                Status
              </p>
              <p className="text-xs text-muted-foreground">Online</p>
            </div>
            <ExternalLink className="h-4 w-4 text-muted-foreground/20 group-hover:text-muted-foreground transition-colors" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}
